"use client";

import { useSSE } from "@/hooks/useSSE";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AlertCircle, CheckCircle2, Loader2, WifiOff, Zap } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

export const SSEStatusIndicator = () => {
  const {
    connected,
    connecting,
    error,
    events,
    lastEvent,
    connectionRetries,
    maxRetries,
    connect,
    disconnect,
    clearAllEvents,
    clearConnectionError,
  } = useSSE({
    autoConnect: false, // Don't auto-connect from the indicator
    autoDisconnect: false, // Don't auto-disconnect
  });

  const getStatusIcon = () => {
    if (connected) {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    }
    if (connecting) {
      return <Loader2 className="w-4 h-4 animate-spin text-blue-600" />;
    }
    if (error) {
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    }
    return <WifiOff className="w-4 h-4 text-gray-400" />;
  };

  const getStatusText = () => {
    if (connected) return "Connected";
    if (connecting) return "Connecting...";
    if (error) return "Error";
    return "Disconnected";
  };

  const getStatusColor = () => {
    if (connected)
      return "bg-green-500/20 text-green-600 hover:bg-green-500/30";
    if (connecting) return "bg-blue-500/20 text-blue-600 hover:bg-blue-500/30";
    if (error) return "bg-red-500/20 text-red-600 hover:bg-red-500/30";
    return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/30";
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 gap-2 text-xs font-medium"
        >
          {getStatusIcon()}
          <span className="hidden sm:inline">Live Updates</span>
          {events.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {events.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span className="font-semibold">Live Updates</span>
            </div>
            <Badge className={getStatusColor()}>{getStatusText()}</Badge>
          </div>

          {/* Connection Info */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Status:</span>
              <span className="font-medium">{getStatusText()}</span>
            </div>
            <div className="flex justify-between">
              <span>Events:</span>
              <span className="font-medium">{events.length}</span>
            </div>
            {connectionRetries > 0 && (
              <div className="flex justify-between">
                <span>Retries:</span>
                <span className="font-medium">
                  {connectionRetries}/{maxRetries}
                </span>
              </div>
            )}
            {lastEvent && (
              <div className="flex justify-between">
                <span>Last Event:</span>
                <span
                  className="font-medium truncate max-w-32"
                  title={lastEvent.type}
                >
                  {lastEvent.type}
                </span>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-sm">
              <div className="flex items-center gap-2 text-red-600 font-medium mb-1">
                <AlertCircle className="w-4 h-4" />
                Connection Error
              </div>
              <p className="text-red-700 text-xs">{error}</p>
            </div>
          )}

          {/* Recent Events */}
          {events.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Recent Events</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllEvents}
                  className="h-6 px-2 text-xs"
                >
                  Clear
                </Button>
              </div>

              <ScrollArea className="h-32 w-full border rounded p-2">
                <div className="space-y-1">
                  {events
                    .slice(-10)
                    .reverse()
                    .map((event, index) => (
                      <div
                        key={`${event.timestamp}-${index}`}
                        className="text-xs p-1 bg-gray-50 rounded"
                      >
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-blue-600 truncate">
                            {event.type}
                          </span>
                          <span className="text-gray-500 text-xs ml-2 flex-shrink-0">
                            {new Date(event.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        {event.data && (
                          <div className="text-gray-600 mt-1 truncate">
                            {typeof event.data === "string"
                              ? event.data
                              : JSON.stringify(event.data).slice(0, 50) + "..."}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            {connected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={disconnect}
                className="flex-1"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={connect}
                disabled={connecting}
                className="flex-1"
              >
                {connecting ? "Connecting..." : "Connect"}
              </Button>
            )}

            {error && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearConnectionError}
                className="flex-1"
              >
                Clear Error
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
